import React, { useEffect, useMemo, useRef, useState } from 'react'
import DataTable, { createTheme } from 'react-data-table-component'
import { Overlay, OverlayTrigger, Tooltip } from 'react-bootstrap'
import ReactPaginate from 'react-paginate'
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  X
} from 'react-feather'

import '@src/assets/scss/ntts-tokens.css'
import './NttsTable.scss'

// RDT theme built on the NTTS tokens, so light/dark follow <html data-theme>
createTheme('ntts', {
  text: {
    primary: 'var(--color-text-secondary)',
    secondary: 'var(--color-text-muted)',
    disabled: 'var(--color-disabled-text)'
  },
  background: { default: 'var(--color-surface)' },
  context: {
    background: 'var(--color-primary-soft)',
    text: 'var(--color-primary-text)'
  },
  divider: { default: 'var(--color-border-subtle)' },
  button: {
    default: 'var(--color-text-muted)',
    hover: 'var(--color-hover)',
    focus: 'var(--color-hover)',
    disabled: 'var(--color-disabled-text)'
  },
  sortFocus: { default: 'var(--color-primary-text)' },
  selected: { default: 'var(--color-primary-soft)', text: 'var(--color-text)' },
  highlightOnHover: {
    default: 'var(--color-table-row-hover)',
    text: 'var(--color-text)'
  },
  striped: { default: 'var(--color-surface-muted)', text: 'var(--color-text)' }
})

// Internal key: RDT falls back to `row.id`, which some APIs repeat or omit
const ROW_KEY = '__nttsRowKey'

const hasValue = (value) =>
  value !== null && value !== undefined && value !== ''

export const EmptyValue = () => <span className="ntts-empty-val">—</span>

let tooltipSeq = 0

/**
 * NttsTooltip — the table's styled tooltip for any element (use it instead of
 * the native `title` attribute). The wrapper span keeps it working on
 * disabled buttons, which don't fire mouse events themselves.
 *
 * <NttsTooltip content="View feedback"><button …/></NttsTooltip>
 */
export const NttsTooltip = ({ content, placement = 'top', children }) => {
  const [id] = useState(() => `ntts-tooltip-${++tooltipSeq}`)
  if (!content) return children
  return (
    <OverlayTrigger
      placement={placement}
      overlay={
        <Tooltip id={id} className="ntts-tooltip">
          {content}
        </Tooltip>
      }
    >
      <span
        className="ntts-tooltip-target"
        // Clicks land on the wrapper itself only over a disabled child; swallow
        // them so they don't bubble to a row click, as a disabled button would
        onClick={(e) => {
          if (e.target === e.currentTarget) e.stopPropagation()
        }}
      >
        {children}
      </span>
    </OverlayTrigger>
  )
}

// Column header text; shows a tooltip only when the label is truncated
const HeaderLabel = ({ children }) => {
  const ref = useRef(null)
  const [show, setShow] = useState(false)

  const isTruncated = () =>
    !!ref.current && ref.current.scrollWidth > ref.current.clientWidth

  return (
    <>
      <span
        ref={ref}
        className="ntts-table__head-label"
        onMouseEnter={() => setShow(isTruncated())}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </span>
      <Overlay target={ref.current} show={show} placement="top">
        {(props) => (
          <Tooltip {...props} className="ntts-tooltip">
            {children}
          </Tooltip>
        )}
      </Overlay>
    </>
  )
}

// Cell text that is actually cut off: ellipsis or line-clamp (both need a
// non-visible overflow, which also rules out ordinary wrapping content)
const isClipped = (el) => {
  if (
    el.scrollWidth <= el.clientWidth + 1 &&
    el.scrollHeight <= el.clientHeight + 1
  ) {
    return false
  }
  const { overflowX, overflowY } = window.getComputedStyle(el)
  return overflowX !== 'visible' || overflowY !== 'visible'
}

// One shared tooltip for every body cell: on hover, shows the full text of
// the truncated element under the pointer, so pages don't add their own
const useCellTooltip = () => {
  const [tip, setTip] = useState(null)
  // Keeps the text while the tooltip fades out
  const lastText = useRef('')
  if (tip) lastText.current = tip.text

  const hide = () => setTip((prev) => (prev ? null : prev))

  const onMouseOver = (e) => {
    const cell = e.target.closest?.('.rdt_TableCell')
    if (!cell) return hide()
    for (let el = e.target; el && el !== cell; el = el.parentElement) {
      if (isClipped(el)) {
        const text = el.textContent.trim()
        if (!text) break
        return setTip((prev) =>
          prev?.target === el ? prev : { target: el, text }
        )
      }
    }
    hide()
  }

  const tooltip = (
    <Overlay target={tip?.target} show={!!tip} placement="top">
      {(props) => (
        <Tooltip {...props} className="ntts-tooltip">
          {lastText.current}
        </Tooltip>
      )}
    </Overlay>
  )

  return {
    handlers: {
      onMouseOver,
      onMouseLeave: hide,
      onScrollCapture: hide
    },
    tooltip
  }
}

// Pages start their fetch in an effect, after the first paint, so `loading`
// is still false on mount and the empty state would flash before the
// skeleton. Treat the table as unsettled until a load has started and
// finished — or a short grace period passes, for tables that never load.
const SETTLE_GRACE_MS = 400

const useSettled = (loading) => {
  const [settled, setSettled] = useState(false)
  const started = useRef(false)

  useEffect(() => {
    if (loading) {
      started.current = true
      return
    }
    if (started.current) {
      setSettled(true)
      return
    }
    const timer = setTimeout(() => setSettled(true), SETTLE_GRACE_MS)
    return () => clearTimeout(timer)
  }, [loading])

  return settled
}

const columnBasis = (column) =>
  column.width
    ? { flex: `0 0 ${column.width}`, width: column.width }
    : { flex: `${column.grow ?? 1} 0 0`, minWidth: column.minWidth || '100px' }

const SkeletonRows = ({ columns, rows }) => (
  <div className="ntts-table__skeleton" aria-hidden="true">
    {Array.from({ length: rows }).map((_, r) => (
      <div key={r} className="ntts-table__skeleton-row">
        {columns.map((column, i) => (
          <div
            key={column.id ?? column.name ?? i}
            className="ntts-table__skeleton-cell"
            style={columnBasis(column)}
          >
            {column.skeleton === 'avatar' ? (
              <>
                <span className="ntts-skel ntts-skel--avatar" />
                <span className="ntts-table__skeleton-lines">
                  <span className="ntts-skel" style={{ width: 120 }} />
                  <span className="ntts-skel" style={{ width: 70 }} />
                </span>
              </>
            ) : (
              <span
                className="ntts-skel"
                style={{ width: `${40 + ((r + i) % 4) * 12}%` }}
              />
            )}
          </div>
        ))}
      </div>
    ))}
  </div>
)

// Groups applied-filter chips by label, keeping first-appearance order
const groupChips = (chips) => {
  const groups = []
  const byLabel = new Map()
  chips.forEach((chip) => {
    const id = chip.label || chip.key
    if (!byLabel.has(id)) {
      const group = { key: `group-${id}`, label: chip.label, chips: [] }
      byLabel.set(id, group)
      groups.push(group)
    }
    byLabel.get(id).chips.push(chip)
  })
  return groups
}

const EmptyState = ({ title, text, actionLabel, onAction }) => (
  <div className="ntts-empty">
    <div className="ntts-empty__title">{title}</div>
    {text && <div className="ntts-empty__text">{text}</div>}
    {actionLabel && onAction && (
      <button type="button" className="ntts-btn" onClick={onAction}>
        {actionLabel}
      </button>
    )}
  </div>
)

const Footer = ({
  pageNo,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50]
}) => {
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const rangeStart = total === 0 ? 0 : (pageNo - 1) * pageSize + 1
  const rangeEnd = Math.min(pageNo * pageSize, total)

  return (
    <div className="ntts-footer">
      {onPageSizeChange && (
        <label className="ntts-footer__size">
          Rows
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      )}
      <span className="ntts-footer__range">
        {rangeStart}–{rangeEnd} of {total}
      </span>
      <ReactPaginate
        pageCount={pageCount}
        forcePage={Math.min(pageNo, pageCount) - 1}
        onPageChange={({ selected }) => onPageChange(selected + 1)}
        marginPagesDisplayed={1}
        pageRangeDisplayed={3}
        breakLabel="…"
        previousLabel={<ChevronLeft aria-label="Previous page" />}
        nextLabel={<ChevronRight aria-label="Next page" />}
        containerClassName="ntts-pager"
        // Classed links: Bootstrap's `a:not([href]):not([class]):hover
        // { color: inherit }` would otherwise recolor them on hover
        pageLinkClassName="ntts-pager__link"
        previousLinkClassName="ntts-pager__link"
        nextLinkClassName="ntts-pager__link"
        breakLinkClassName="ntts-pager__link"
        activeClassName="is-active"
        previousClassName="is-nav"
        nextClassName="is-nav"
        disabledClassName="is-disabled"
      />
    </div>
  )
}

/**
 * Shared NTTS table: card + toolbar + applied-filter chips + data table
 * (react-data-table-component) + skeleton/empty states + pagination.
 *
 * Columns are regular RDT columns. Extras:
 *  - `skeleton: 'avatar'` renders an avatar + two lines while loading
 *  - a column without `cell` shows its selector value, or "—" when empty
 *  - truncated cell text (e.g. .ntts-ellipsis or a line clamp) gets a tooltip
 *    with the full text automatically; don't add `title` for that
 *
 * @param {object}   props.search      { value, onChange(value), placeholder }
 * @param {node}     props.actions     extra toolbar controls (e.g. Filters button)
 * @param {node}     props.result      right-aligned toolbar summary
 * @param {array}    props.filters     applied filter chips: { key, label, value, onRemove, onClearGroup }
 *                                     chips sharing a `label` render as one group; the
 *                                     group gets its own × when a chip has `onClearGroup`
 * @param {function} props.onClearFilters  "Clear all" handler
 * @param {function} props.onAddFilter "+ Filter" handler shown in the chips row
 * @param {object}   props.pagination  { pageNo, pageSize, total, onPageChange(page), onPageSizeChange(size), pageSizeOptions }
 * @param {object}   props.empty       { title, text, actionLabel, onAction }
 * @param {string}   props.tableHeight card height override (default fills the viewport below the page title, see SCSS)
 */
const NttsTable = ({
  columns,
  data,
  loading = false,
  onRowClicked,
  onSort,
  sortServer = false,
  stickyFirstColumn = true,
  minWidth,
  skeletonRows = 8,
  search,
  actions,
  result,
  filters = [],
  onClearFilters,
  onAddFilter,
  pagination,
  empty = { title: 'No records found' },
  ariaLabel,
  className = '',
  tableHeight,
  ...rest
}) => {
  const rows = useMemo(
    () => (data || []).map((row, i) => ({ ...row, [ROW_KEY]: i })),
    [data]
  )
  const cellTooltip = useCellTooltip()
  const settled = useSettled(loading)
  const showLoading = loading || (!settled && rows.length === 0)

  // Wrap each cell so a click anywhere in it opens the row — RDT only fires
  // onRowClicked for elements tagged data-tag="allowRowEvents"
  const tableColumns = useMemo(
    () =>
      columns.map((column, colIndex) => ({
        ...column,
        name:
          typeof column.name === 'string' ? (
            <HeaderLabel>{column.name}</HeaderLabel>
          ) : (
            column.name
          ),
        cell: (row, rowIndex, col, id) => {
          let content
          if (column.cell) {
            content = column.cell(row, rowIndex, col, id)
          } else {
            const value = column.selector?.(row)
            content = hasValue(value) ? value : <EmptyValue />
          }
          if (!onRowClicked) {
            return <div className="ntts-table__cell">{content}</div>
          }
          const isFirst = colIndex === 0
          return (
            <div
              className="ntts-table__cell"
              onClick={() => onRowClicked(row)}
              {...(isFirst
                ? {
                    role: 'link',
                    tabIndex: 0,
                    onKeyDown: (e) => {
                      if (e.key === 'Enter') onRowClicked(row)
                    }
                  }
                : {})}
            >
              {content}
            </div>
          )
        }
      })),
    [columns, onRowClicked]
  )

  const showToolbar = search || actions || result
  const total = Number(pagination?.total) || 0

  return (
    <section
      className={`ntts-card ${className}`}
      style={tableHeight ? { height: tableHeight } : undefined}
    >
      {showToolbar && (
        <div className="ntts-card__toolbar">
          {search && (
            <div className="ntts-search">
              <Search />
              <input
                type="text"
                value={search.value}
                placeholder={search.placeholder || 'Search…'}
                aria-label="Search"
                onChange={(e) => search.onChange(e.target.value)}
              />
              {search.value && (
                <button
                  type="button"
                  className="ntts-search__clear"
                  onClick={() => search.onChange('')}
                  aria-label="Clear search"
                >
                  <X />
                </button>
              )}
            </div>
          )}
          {actions}
          {result && <div className="ntts-card__result">{result}</div>}
        </div>
      )}

      {filters.length > 0 && (
        <div className="ntts-chips">
          <span className="ntts-chips__label">
            {filters.length} {filters.length === 1 ? 'filter' : 'filters'}
          </span>
          {groupChips(filters).map((group) => {
            const onClearGroup = group.chips.find(
              (chip) => chip.onClearGroup
            )?.onClearGroup
            return (
              <span key={group.key} className="ntts-chip-group">
                {group.label && (
                  <span className="ntts-chip-group__key">{group.label}</span>
                )}
                {group.chips.map((chip) => (
                  <span
                    key={chip.key}
                    className={`ntts-chip ${
                      chip.onRemove ? '' : 'ntts-chip--static'
                    }`}
                  >
                    <span className="ntts-chip__value">{chip.value}</span>
                    {chip.onRemove && (
                      <button
                        type="button"
                        className="ntts-chip__x"
                        aria-label={`Remove ${chip.value}`}
                        onClick={chip.onRemove}
                      >
                        <X />
                      </button>
                    )}
                  </span>
                ))}
                {onClearGroup && (
                  <button
                    type="button"
                    className="ntts-chip-group__x"
                    aria-label={`Clear ${group.label || 'filter'}`}
                    onClick={onClearGroup}
                  >
                    <X />
                  </button>
                )}
              </span>
            )
          })}
          {onAddFilter && (
            <button
              type="button"
              className="ntts-chips__add"
              onClick={onAddFilter}
            >
              <Plus />
              Filter
            </button>
          )}
          {onClearFilters && (
            <button
              type="button"
              className="ntts-link"
              onClick={onClearFilters}
            >
              Clear all
            </button>
          )}
        </div>
      )}

      <div
        className={`ntts-table ${
          stickyFirstColumn ? 'ntts-table--sticky' : ''
        } ${onRowClicked ? 'ntts-table--clickable' : ''}`}
        style={minWidth ? { '--ntts-table-min-width': minWidth } : undefined}
        aria-label={ariaLabel}
        {...cellTooltip.handlers}
      >
        <DataTable
          noHeader
          responsive
          fixedHeader
          fixedHeaderScrollHeight="100%"
          persistTableHead
          theme="ntts"
          highlightOnHover
          columns={tableColumns}
          data={rows}
          keyField={ROW_KEY}
          sortIcon={<ChevronDown />}
          onSort={onSort}
          sortServer={sortServer}
          progressPending={showLoading}
          progressComponent={
            <SkeletonRows columns={columns} rows={skeletonRows} />
          }
          noDataComponent={<EmptyState {...empty} />}
          {...rest}
        />
        {cellTooltip.tooltip}
      </div>

      {pagination && total > 0 && <Footer {...pagination} total={total} />}
    </section>
  )
}

export default NttsTable
